import { Injectable } from "@angular/core";
import { AccountType, IAccount, IAccountMappingTemplateGroup, IAccountMappingTemplateGroupItemFLI, IFinStatements } from "@modeso/types__aumico-lib-annualreport-be";
import { I18NextService } from "angular-i18next";
import { cloneDeep } from 'lodash-es';
import { NumberUtils } from "../../../../aumico-lib-core-fe/src/lib";

@Injectable()
export class AutomaticTransferOfAnnualResultService {

    constructor(
        private readonly localizedTexts: I18NextService,

    ) { }

    applyAutomaticTransferOfAnnualResultOnFinancialStatements(existingFinStatements: IFinStatements[], previousAnnualProfitForMissingPeriod: number | undefined, shouldUsePreviousAnnualProfitFromCurrentIncomeStatement: boolean, reportLocale?: string): IFinStatements[] {
        const modifiedFinStatements: IFinStatements[] = cloneDeep<IFinStatements[]>(existingFinStatements);
        const balanceTemplate = modifiedFinStatements[0].templateAndAccountsMapping.template;
        const mainBalanceGroup = balanceTemplate.group;
        const currentAnnualProfitResult = modifiedFinStatements[1].templateAndAccountsMapping.template.group.calculatedClosingBalance;

        let previousAnnualProfitResult = 0;
        if (shouldUsePreviousAnnualProfitFromCurrentIncomeStatement) {
            previousAnnualProfitResult = modifiedFinStatements[1].templateAndAccountsMapping.template.group.calculatedOpeningBalance;
        } else if (previousAnnualProfitForMissingPeriod != null) {
            previousAnnualProfitResult = previousAnnualProfitForMissingPeriod;
        }

        const profitFli = balanceTemplate.profitOfTheYear;
        this.mapAnnualProfitToBalanceGroup(
            mainBalanceGroup,
            currentAnnualProfitResult,
            previousAnnualProfitResult,
            profitFli,
            undefined,
            reportLocale
        );
        return modifiedFinStatements;
    }

    mapAnnualProfitToBalanceGroup(mainBalanceGroup: IAccountMappingTemplateGroup, currentAnnualProfitResult: number, previousAnnualProfitResult: number, profitFli: string, editPreviousAnnualProfitResultNotAllowed?: boolean, reportLocale?: string): void {

        mainBalanceGroup.groups.forEach((group: IAccountMappingTemplateGroup) => {
            if (group.items != null) {
                this.setAnnualProfitToBalanceProfitFli(
                    group,
                    currentAnnualProfitResult,
                    previousAnnualProfitResult,
                    profitFli,
                    editPreviousAnnualProfitResultNotAllowed,
                    reportLocale
                );
            }

            // in case groups have groups nested inside them
            if (group.groups != null) {
                this.mapAnnualProfitToBalanceGroup(
                    group,
                    currentAnnualProfitResult,
                    previousAnnualProfitResult,
                    profitFli,
                    editPreviousAnnualProfitResultNotAllowed,
                    reportLocale
                );
            }
        });
    }

    private setAnnualProfitToBalanceProfitFli(
        balanceGroup: IAccountMappingTemplateGroup,
        currentAnnualProfitResult: number,
        previousAnnualProfitResult: number,
        profitFli: string,
        editPreviousAnnualProfitResultNotAllowed?: boolean,
        reportLocale?: string
    ): void {
        balanceGroup.items.forEach((fli) => {
            // If any SPECIAL_ACCOUNT_DELETE_ON_MAPPING account exists on other FLIs, remove them
            if (fli.name !== profitFli && fli.accounts != null && fli.accounts.some(account => account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING)) {
                fli.accounts = fli.accounts.filter(account => account.type !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING);
            }

            if (fli.name === profitFli) {
                this.mapAnnualProfitAccountToProfitFli(
                    fli as IAccountMappingTemplateGroupItemFLI,
                    currentAnnualProfitResult,
                    previousAnnualProfitResult,
                    editPreviousAnnualProfitResultNotAllowed,
                    reportLocale
                );
            }
        });
    }

    private mapAnnualProfitAccountToProfitFli(
        fli: IAccountMappingTemplateGroupItemFLI,
        currentAnnualProfitResult: number,
        previousAnnualProfitResult: number,
        editPreviousAnnualProfitResultNotAllowed?: boolean,
        reportLocale?: string
    ): void {
        if (editPreviousAnnualProfitResultNotAllowed) {
            const existingProfitAccount = fli.accounts?.find((account) =>
                account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING
            );
            if (existingProfitAccount != null) {
                previousAnnualProfitResult = existingProfitAccount.calculatedOpeningBalance;
            }
        }

        if (fli.accounts != null && fli.accounts.length > 0) {

            // Need to empty existing accounts before adding new special account
            fli.accounts = [];

        }

        const transferredProfitAccount: IAccount = {
            accountNumber: '',
            name: this.localizedTexts.t(
                'aumico_templates_account_mapping_form_transferred_profit_account_name',
                { lng: reportLocale },
            ) as string,
            type: AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING,
            openingBalance: NumberUtils.roundNumber(previousAnnualProfitResult),
            closingBalance: NumberUtils.roundNumber(currentAnnualProfitResult),
            calculatedOpeningBalance: NumberUtils.roundNumber(previousAnnualProfitResult),
            calculatedClosingBalance: NumberUtils.roundNumber(currentAnnualProfitResult),
        };

        fli.accounts = [transferredProfitAccount];
    }

    moveAnnualProfitAccountToAnotherFli(mainBalanceGroup: IAccountMappingTemplateGroup, profitFli: string): void {
        mainBalanceGroup.groups.forEach((group: IAccountMappingTemplateGroup) => {
            if (group.items != null) {
                this.setExistingAnnualProfitAccountToBalanceProfitFli(
                    group,
                    profitFli,
                );
            }

            // in case groups have groups nested inside them
            if (group.groups != null) {
                this.moveAnnualProfitAccountToAnotherFli(
                    group,
                    profitFli,
                );
            }
        });
    }

    private setExistingAnnualProfitAccountToBalanceProfitFli(balanceGroup: IAccountMappingTemplateGroup, profitFli: string): void {
        let existingAnnualProfitAccount: IAccount | null = null;
        balanceGroup.items.forEach((fli) => {
            // If any SPECIAL_ACCOUNT_DELETE_ON_MAPPING account exists on other FLIs, remove them
            if (fli.name !== profitFli && fli.accounts?.some(account => account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING)) {
                existingAnnualProfitAccount = cloneDeep<IAccount>(fli.accounts.find(account => account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING));
                fli.accounts = fli.accounts.filter(account => account.type !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING);
            }
        });

        balanceGroup.items.forEach((fli) => {

            if (fli.name === profitFli) {
                this.mapAnnualProfitAccountToProfitFli(
                    fli as IAccountMappingTemplateGroupItemFLI,
                    existingAnnualProfitAccount?.calculatedClosingBalance ?? 0,
                    existingAnnualProfitAccount?.calculatedOpeningBalance ?? 0,
                    undefined,
                    undefined,
                );
            }
        });
    }
}